import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { creditorsAPI } from '../../services/api';
import { formatApiError } from '../../utils/errorMessage';
import { formatMoney } from '../../utils/money';

const money = (v) => formatMoney(v);

// Normalises the creditor liabilities response ({ accounts, legacy }); an
// older array response is treated as legacy rows.
export const splitLiabilities = (data) => {
  if (Array.isArray(data)) return { accounts: [], legacy: data };
  return {
    accounts: Array.isArray(data?.accounts) ? data.accounts : [],
    legacy: Array.isArray(data?.legacy) ? data.legacy : [],
  };
};

const typeLabel = (t) => (t ? String(t).replace(/_/g, ' ') : '');

// Liabilities popup on the Creditors page: the creditor's liability accounts
// (same rows the count and total use), with legacy manual rows separate.
const CreditorLiabilitiesModal = ({ isOpen, onClose, creditor, selectedBusiness }) => {
  const [data, setData] = useState({ accounts: [], legacy: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = selectedBusiness ? { business_id: selectedBusiness } : undefined;
      const response = await creditorsAPI.getLiabilities(creditor.id, params);
      setData(splitLiabilities(response.data));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen && creditor) load(); }, [isOpen, creditor, selectedBusiness]);

  if (!isOpen) return null;
  const { accounts, legacy } = data;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Liabilities for {creditor?.creditor_name}</h2>
            <p className="text-sm text-gray-500 mt-1">Total Balance: {money(creditor?.total_balance)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">X</button>
        </div>
        <div className="p-6 space-y-6">
          {loading && <p className="text-center text-gray-600 py-8">Loading liabilities...</p>}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
              <p className="text-sm text-red-600">{formatApiError(error, 'Failed to load liabilities')}</p>
              <button type="button" onClick={load} className="mt-2 text-sm font-medium text-red-600">Try Again</button>
            </div>
          )}
          {!loading && !error && accounts.length === 0 && legacy.length === 0 && (
            <p className="text-center text-gray-600 py-8">No liabilities found for this creditor.</p>
          )}
          {!loading && !error && accounts.length > 0 && (
            <ul className="space-y-3" data-testid="liability-accounts">
              {accounts.map((a) => (
                <li key={a.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-start gap-4">
                  <div>
                    <Link to={`/accounts?account_id=${a.id}`} className="font-medium text-blue-700 hover:underline">{a.account_name}</Link>
                    <p className="text-sm text-gray-500 mt-1">Type: {typeLabel(a.account_type)}{a.business_name ? ` - ${a.business_name}` : ''}</p>
                    <p className="text-sm text-gray-600 mt-1">{a.linked_to ? `Linked to: ${a.linked_to}` : 'Not linked to a property or asset'}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{money(a.amount_owed)}</div>
                    {a.amount_due != null && <div className="text-sm text-gray-600 mt-1">Due: {money(a.amount_due)}</div>}
                    {a.next_due_date && <div className="text-sm text-gray-500 mt-1">Next Due: {a.next_due_date}</div>}
                    <Link to={`/transactions?account_id=${a.id}`} className="inline-block mt-2 text-sm text-blue-700 hover:underline">View transactions</Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!loading && !error && legacy.length > 0 && (
            <section data-testid="legacy-liabilities">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Legacy (manual)</h3>
              <ul className="space-y-2">
                {legacy.map((l) => (
                  <li key={`legacy-${l.id}`} className="border border-dashed border-gray-300 rounded p-3 flex justify-between text-sm">
                    <span>{l.liability_name}{l.liability_type ? ` (${typeLabel(l.liability_type)})` : ''}</span>
                    <span>{money(l.current_balance)}{l.next_due_date ? ` - next due ${l.next_due_date}` : ''}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditorLiabilitiesModal;
